import React from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';

import { Container, Content } from './Book.styled';
import { ProgressTracker } from './components/ProgressTracker/ProgressTracker';
import { AuthFlowLayout } from 'components/AuthFlowLayout/AuthFlowLayout';
import { Loader } from 'components/Loader/Loader';
import { useBookingState } from 'kb-shared/booking/hooks/useBookingState';
import { StepType } from 'kb-shared/booking/types/types';
import { bookingAnalytics } from 'kb-shared/booking/utils/bookingAnalytics';
import { BookingSearchParams } from 'kb-shared/booking/utils/bookingSearchParams';
import { getAppointmentType } from 'kb-shared/booking/utils/getters';
import { toIntOrNull } from 'kb-shared/booking/utils/validation';
import { RoutesPath, pageUrl } from 'utilities/pageUrl';
import { useBreakpoints } from 'utilities/useBreakpoints';

import { getPageTitle } from './Book.utils';
import { ConfirmStep } from './steps/Confirm/Confirm';
import { InsuranceStep } from './steps/Insurance/Insurance';
import { LocationStep } from './steps/Location/Location';
import { isHomeMarketBookingRequired } from './steps/Location/Location.utils';
import { SuccessStep } from './steps/Success/Success';
import { TimeStep } from './steps/Time/Time';

export const Book = (): React.ReactElement | null => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    loading,
    error,
    entities,
    insuranceStepCompleted,
    bookingParams,
    stepSupported,
    calculatedStep
  } = useBookingState();

  const { isDesktop } = useBreakpoints();

  const pageTitle = getPageTitle({
    partnerClinicSearch: bookingParams.get('partner_clinic_search') ?? '',
    selectedProduct: entities.selectedProduct || null,
    stepType: calculatedStep as StepType
  });

  const appointmentType = getAppointmentType(entities.selectedProduct);

  const {
    selectedClinic,
    selectedLab,
    selectedProduct,
    selectedTimeSlot,
    purchasedProduct,
    allAppointmentTypes
  } = entities;

  const requireHomeMarketBooking = isHomeMarketBookingRequired(appointmentType ?? undefined);

  const renderLocationStep = () => (
    <LocationStep
      onBack={() => navigate(-1)}
      appointmentType={appointmentType || undefined}
      selectedAppointmentTypes={allAppointmentTypes ?? []}
      partnerClinicSearch={bookingParams.get('partner_clinic_search') ?? ''}
      selectedClinic={selectedClinic}
      selectedProduct={selectedProduct}
      // @ts-ignore-next-line
      selectedLab={selectedLab}
      onSelectClinic={(clinic, inPersonAppointmentType) => {
        const params = new BookingSearchParams(location.search);
        params.merge({
          clinic_id: clinic?.id.toString() ?? '',
          step: 'time',
          appointment_id: inPersonAppointmentType?.id || bookingParams.get('appointment_id') || ''
        });
        bookingAnalytics.track.clinicSelected(clinic);
        navigate(`${RoutesPath.BOOKING_BOOK}?${params}`);
      }}
      onSelectLab={lab => {
        const params = new BookingSearchParams(location.search);
        // remain on the same step to allow user to select a clinic
        params.set('lab_id', lab?.id ?? '');
        bookingAnalytics.track.labSelected(lab?.id);
        navigate(`${RoutesPath.BOOKING_BOOK}?${params}`, {
          replace: requireHomeMarketBooking ? true : false
        });
      }}
      onContactUsClick={() => {
        const params = new BookingSearchParams(location.search);
        params.set('partner_clinic_search', 'search');
        bookingAnalytics.track.partnerClinicSearchStarted();
        navigate(`${RoutesPath.BOOKING_BOOK}?${params}`);
      }}
    />
  );

  const renderTimeStep = () => (
    // When changing also check the <TimeStep /> component of src/screens/CreateAccount/CreateAccount.tsx
    <TimeStep
      onBack={() => navigate(-1)}
      date={bookingParams.get('date') ?? undefined}
      providerId={toIntOrNull(bookingParams.get('provider_id')) ?? undefined}
      appointmentType={appointmentType || undefined}
      selectedClinic={selectedClinic}
      selectedLab={selectedLab}
      selectedTimeSlot={selectedTimeSlot}
      onTimeSlotSelect={timeSlot => {
        const params = new BookingSearchParams(location.search);
        params.merge({
          time_slot_id: timeSlot.id,
          step: insuranceStepCompleted ? 'confirm' : 'insurance',
          appointment_id: String(timeSlot.appointmentTypeId) ?? '',
          appointment_types: 'all'
        });
        bookingAnalytics.track.timeSlotSelected(timeSlot);
        if (insuranceStepCompleted) {
          bookingAnalytics.page.confirmPurchase();
        } else {
          bookingAnalytics.page.enterInsuranceInformation();
        }
        navigate(`${RoutesPath.BOOKING_BOOK}?${params}`);
      }}
    />
  );

  const renderInsuranceStep = () => (
    <InsuranceStep
      onBack={() => navigate(-1)}
      onNextStep={() => {
        const params = new BookingSearchParams(location.search);
        params.set('step', 'confirm');
        bookingAnalytics.page.confirmPurchase();
        navigate(`${RoutesPath.BOOKING_BOOK}?${params}`);
      }}
    />
  );

  const renderConfirmStep = () => (
    <ConfirmStep
      onBack={() => navigate(-1)}
      selectedClinic={selectedClinic}
      selectedTimeSlot={selectedTimeSlot}
      selectedProduct={selectedProduct}
      onBookAppointment={appointment => {
        const params = new BookingSearchParams(location.search);
        params.merge({
          step: 'success',
          confirmed_appointment_id: appointment.id
        });
        navigate(`${RoutesPath.BOOKING_BOOK}?${params}`);
      }}
    />
  );

  const renderSuccessStep = () => <SuccessStep purchasedProduct={purchasedProduct} />;

  if (loading || error) return <Loader container />;

  // redirect to booking-quiz if we don't know what to show
  if (!bookingParams.containsBookingDataParams() || !stepSupported) {
    return <Navigate to={pageUrl.booking.bookingQuiz()} />;
  }

  return (
    <AuthFlowLayout
      title={isDesktop ? '' : pageTitle}
      renderAboveTitleContent={() =>
        !isDesktop && calculatedStep !== 'success' && <ProgressTracker step={calculatedStep} />
      }
      renderMainContent={() => (
        <Container>
          <Content>
            {calculatedStep === 'location' && renderLocationStep()}
            {calculatedStep === 'time' && renderTimeStep()}
            {calculatedStep === 'insurance' && renderInsuranceStep()}
            {calculatedStep === 'confirm' && renderConfirmStep()}
            {calculatedStep === 'success' && renderSuccessStep()}
          </Content>
        </Container>
      )}
    />
  );
};
